import { useMemo } from "react";
import type {
  CrossProjectBoardResponse,
  KanbanColumn,
  TaskListItem,
  Assignee,
  BoardProject,
  TaskStatus,
} from "../types";

export interface ProjectBoardView {
  project: BoardProject;
  columns: KanbanColumn[];
  backlogColumn: KanbanColumn;
  assignees: Assignee[];
}

interface CrossProjectBoardResult {
  mergedColumns: KanbanColumn[];
  backlogTasks: TaskListItem[];
  projectIndexMap: Map<number, number>;
  allAssignees: Assignee[];
  totalTaskCount: number;
  projectBoards: ProjectBoardView[];
}

const MAIN_STATUSES: TaskStatus[] = ["todo", "in_progress", "blocked", "done"];

export function useCrossProjectBoard(
  data: CrossProjectBoardResponse | undefined,
): CrossProjectBoardResult {
  return useMemo(() => {
    if (!data || data.boards.length === 0) {
      return {
        mergedColumns: MAIN_STATUSES.map((status) => ({
          status,
          label: status,
          tasks: [],
        })),
        backlogTasks: [],
        projectIndexMap: new Map(),
        allAssignees: [],
        totalTaskCount: 0,
        projectBoards: [],
      };
    }

    const columnMap = new Map<TaskStatus, TaskListItem[]>();
    for (const status of MAIN_STATUSES) {
      columnMap.set(status, []);
    }

    const backlogTasks: TaskListItem[] = [];
    const projectIndexMap = new Map<number, number>();
    const assigneeMap = new Map<number, Assignee>();
    let totalTaskCount = 0;
    const projectBoards: ProjectBoardView[] = [];

    data.boards.forEach((board, boardIndex) => {
      for (const assignee of board.assignees) {
        if (!assigneeMap.has(assignee.id)) {
          assigneeMap.set(assignee.id, assignee);
        }
      }

      const perProjectColumns: KanbanColumn[] = MAIN_STATUSES.map((status) => ({
        status,
        label: status,
        tasks: [],
      }));

      const projectBacklog: KanbanColumn = { status: "backlog", label: "backlog", tasks: [] };

      for (const column of board.columns) {
        for (const task of column.tasks) {
          projectIndexMap.set(task.id, boardIndex);
          totalTaskCount++;

          if (column.status === "backlog") {
            backlogTasks.push(task);
            projectBacklog.tasks.push(task);
          } else {
            const bucket = columnMap.get(column.status);
            if (bucket) {
              bucket.push(task);
            }
            const perProjectCol = perProjectColumns.find((c) => c.status === column.status);
            if (perProjectCol) {
              perProjectCol.tasks.push(task);
            }
          }
        }
      }

      projectBoards.push({
        project: board.project,
        columns: perProjectColumns,
        backlogColumn: projectBacklog,
        assignees: board.assignees,
      });
    });

    const mergedColumns: KanbanColumn[] = MAIN_STATUSES.map((status) => ({
      status,
      label: status,
      tasks: columnMap.get(status) ?? [],
    }));

    return {
      mergedColumns,
      backlogTasks,
      projectIndexMap,
      allAssignees: Array.from(assigneeMap.values()),
      totalTaskCount,
      projectBoards,
    };
  }, [data]);
}
